import { type BoardSourceFieldValue, type PortalSettings, type FieldValues } from '@gorilla/portal-shared/src/schemas';
import { useSingleBoard, useSubboard } from '../shared/hooks/use-boards';
import { extractBoardNameAndErrorMessage, extractConnectBoardColumnErrorMessage } from '../components/board-sources/common';
import { SectionDescriptor } from '@gorilla/portal-shared/src/types';

type UseBoardSourceResolverOptions = {
  portalSettings: PortalSettings;
  sectionDescriptor: SectionDescriptor;
  clientBoardSubitemsBoardId: number | null;
  sectionFieldValues: FieldValues;
};

export function useBoardSourceResolver({
  portalSettings,
  sectionDescriptor,
  clientBoardSubitemsBoardId,
  sectionFieldValues,
}: UseBoardSourceResolverOptions) {
  const errors: string[] = [];
  const boardSourceFieldDescriptor = sectionDescriptor.fields.find((field) => field.type === 'board-source')!;
  const hasSubitemsField = sectionDescriptor.fields.some((field) => field.type === 'subitems');
  const boardSourceValue = boardSourceFieldDescriptor
    ? (sectionFieldValues[boardSourceFieldDescriptor.id] as BoardSourceFieldValue | undefined)
    : undefined;
  const boardSource =
    boardSourceValue?.type === 'subitems'
      ? ({ type: 'subitems' } as const)
      : (portalSettings.boardSources || []).find((source) => boardSourceValue && source.id === boardSourceValue?.boardSourceId);
  let boardId: number | undefined = undefined;

  if (boardSource) {
    if (boardSource.type === 'client-board') {
      boardId = boardSource.templateBoardId;
    } else if (boardSource.type === 'shared-client-board') {
      boardId = boardSource.boardId;
    } else if (boardSource.type === 'board') {
      boardId = boardSource.boardId;
    } else if (boardSource.type === 'subitems') {
      boardId = clientBoardSubitemsBoardId || undefined;
    }
  }

  const supportsSubitems = hasSubitemsField && boardSource && boardSource?.type !== 'subitems';
  const singleBoardResult = useSingleBoard(boardId ? boardId : undefined);
  const { board, isLoading } = singleBoardResult;
  const { subboard, isLoading: isLoadingSubboard } = useSubboard(boardId && supportsSubitems ? boardId : undefined);

  if (boardSource && boardSource.type === 'subitems') {
    if (!boardId || (!isLoading && board === null)) {
      errors.push('No subitems found. Please create some subitems and then try again.');
    } else if (singleBoardResult.error) {
      errors.push('Error loading subitems board.');
    }
  } else {
    const { boardErrorMessage } = extractBoardNameAndErrorMessage(singleBoardResult, boardId);

    if (boardErrorMessage) {
      errors.push(boardErrorMessage);
    }
  }

  if (boardSource && boardSource.type === 'shared-client-board') {
    const connectBoardColumnErrorMessage = extractConnectBoardColumnErrorMessage(singleBoardResult, boardSource.clientColumnId);

    if (connectBoardColumnErrorMessage) {
      errors.push(connectBoardColumnErrorMessage);
    }
  }

  return {
    errors,
    board,
    boardId,
    isLoading: isLoading || isLoadingSubboard,
    supportsSubitems,
    subboard,
    isLoadingSubboard,
    boardSourceType: boardSource?.type,
  };
}
